import axios, { AxiosHeaders, isAxiosError } from 'axios';
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

/** Maps axios / ASP.NET ProblemDetails / our API shape to a single user-visible string. */
export function readApiErrorMessage(error: unknown, fallback: string): string {
  if (!isAxiosError(error)) {
    return fallback;
  }
  const data = error.response?.data as Record<string, unknown> | undefined;
  if (!data) {
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      return (
        'Cannot reach the API (network error). Rebuild the app with REACT_APP_API_URL pointing to your API ' +
        '(e.g. https://your-server/api), use the same http/https as this page, and ensure the API is running and CORS allows this origin.'
      );
    }
    return error.message || fallback;
  }
  const nested = data.error as { message?: string } | undefined;
  if (nested?.message && typeof nested.message === 'string') {
    return nested.message;
  }
  if (typeof data.error === 'string') {
    return data.error;
  }
  if (typeof data.message === 'string') {
    return data.message;
  }
  if (typeof data.title === 'string') {
    const errors = data.errors as Record<string, string[] | string> | undefined;
    if (errors && typeof errors === 'object') {
      for (const key of Object.keys(errors)) {
        const v = errors[key];
        const first = Array.isArray(v) ? v[0] : v;
        if (typeof first === 'string') {
          return first;
        }
      }
    }
    return data.title;
  }
  return fallback;
}

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Prime CSRF cookie/header before the first POST/PUT/PATCH (avoids 403 on /api/sessions). */
export async function ensureCsrfReady(): Promise<void> {
  const response = await api.get('/auth/profile');
  const csrfToken = response.headers['x-csrf-token'] as string | undefined;
  if (csrfToken) {
    (window as unknown as { __csrfToken?: string }).__csrfToken = csrfToken;
  }
}

// Add request interceptor to include auth token and CSRF token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Include CSRF token for state-changing requests (POST, PUT, DELETE, PATCH)
    const stateChangingMethods = ['POST', 'PUT', 'DELETE', 'PATCH'];
    if (config.data instanceof FormData) {
      if (config.headers instanceof AxiosHeaders) {
        config.headers.delete('Content-Type');
      } else {
        const h = config.headers as Record<string, string> | undefined;
        if (h) {
          delete h['Content-Type'];
          delete h['content-type'];
        }
      }
    }

    if (stateChangingMethods.includes(config.method?.toUpperCase() || '')) {
      // Try to get CSRF token from multiple sources
      let csrfToken: string | null = null;
      
      // 1. Try from window object (set by response interceptor)
      if ((window as any).__csrfToken) {
        csrfToken = (window as any).__csrfToken;
      }
      
      // 2. Try from cookies
      if (!csrfToken) {
        const cookies = document.cookie.split(';');
        const csrfCookie = cookies.find(c => c.trim().startsWith('csrf-token='));
        if (csrfCookie) {
          csrfToken = csrfCookie.split('=')[1].trim();
        }
      }
      
      // 3. Include in header if found
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors and capture CSRF tokens
api.interceptors.response.use(
  (response) => {
    // Capture CSRF token from response headers
    const csrfToken = response.headers['x-csrf-token'];
    if (csrfToken) {
      (window as any).__csrfToken = csrfToken;
      // Also set as cookie if provided
      if (response.headers['set-cookie']) {
        // Cookie is automatically handled by browser
      }
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('token_expires');
      window.location.href = '/login';
    } else if (error.response?.status === 403) {
      const data = error.response?.data as { error?: { message?: string; code?: string } } | undefined;
      const errorCode = data?.error?.code;
      const errorMessage = data?.error?.message || (error.response?.data as { message?: string })?.message || '';

      if (errorCode === 'must_change_password') {
        const raw = localStorage.getItem('user');
        if (raw) {
          try {
            const parsed = JSON.parse(raw) as Record<string, unknown>;
            parsed.mustChangePassword = true;
            localStorage.setItem('user', JSON.stringify(parsed));
          } catch {
            /* ignore */
          }
        }
        if (!window.location.pathname.includes('force-change-password')) {
          window.location.href = '/force-change-password';
        }
        return Promise.reject(error);
      }

      const csrfError =
        errorCode === 'csrf' ||
        errorMessage.includes('CSRF') ||
        errorMessage.includes('csrf') ||
        (typeof error.response?.data === 'object' &&
          error.response?.data !== null &&
          'error' in (error.response.data as object) &&
          String((error.response.data as { error?: unknown }).error).toLowerCase().includes('csrf'));

      if (csrfError) {
        console.warn('CSRF token error detected, refreshing page to get new token...');
        // Clear stored CSRF token
        (window as any).__csrfToken = null;
        // Reload page to get fresh token
        window.location.reload();
        return Promise.reject(error);
      }
      
      // Permission denied errors
      if (errorMessage.includes('Permission') || errorMessage.includes('Admin')) {
        console.warn('Permission denied:', errorMessage);
        toast.error(errorMessage || 'Permission denied');
      }
    }
    return Promise.reject(error);
  }
);

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  branchIds?: number[];
  mustChangePassword?: boolean;
  createdAt?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<void>;
  completePasswordChange: (token: string, user: AuthUser) => void;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const warmUpCsrf = async () => {
    try {
      await ensureCsrfReady();
    } catch {
      // Non-blocking best-effort; normal API calls still show real errors if auth is broken.
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const tokenExpires = localStorage.getItem('token_expires');

    // Check if token has expired
    if (tokenExpires) {
      const expiresAt = parseInt(tokenExpires);
      if (Date.now() > expiresAt) {
        // Token expired, clear storage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('token_expires');
        setLoading(false);
        return;
      }
    }

    if (token && userData) {
      try {
        setUser(JSON.parse(userData) as AuthUser);
        void warmUpCsrf();
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('token_expires');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { token, user: userData } = response.data;

      // Store token securely
      // TODO: Migrate to HttpOnly cookies for better security
      // For now, we'll add additional security measures
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Set token expiration time (7 days default)
      const expiresIn = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
      localStorage.setItem('token_expires', (Date.now() + expiresIn).toString());
      
      setUser(userData as AuthUser);
      void warmUpCsrf();

      if (userData?.mustChangePassword) {
        toast.success('Signed in. Please set a new password.');
      } else {
        toast.success('Login successful!');
      }
    } catch (error: unknown) {
      toast.error(readApiErrorMessage(error, 'Login failed'));
      throw error;
    }
  };

  const completePasswordChange = (token: string, nextUser: AuthUser) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(nextUser));
    const expiresIn = 7 * 24 * 60 * 60 * 1000;
    localStorage.setItem('token_expires', (Date.now() + expiresIn).toString());
    setUser(nextUser);
    (window as unknown as { __csrfToken?: string }).__csrfToken = undefined;
    void warmUpCsrf();
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('token_expires');
    setUser(null);
    toast.success('Logged out successfully');
  };

  return (
    <AuthContext.Provider value={{ user, login, completePasswordChange, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

